import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { isEditorEmpty } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import { IRect } from '@tencent/alloy-graphics/lib/types/path/base/interface';
import { BaseShape as ShapeGraphic } from '@tencent/alloy-graphics/lib/types/path/shape/base-shape';
import {
    BoxKind, getTextLayout, traverseLayoutBoxWithHandlers,
    IDocumentBox, IParagraphBox, ITextBox, ITextLineBox,
} from '@tencent/alloy-editor';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { EditorDataModel } from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model';
import { deepCopy } from '@src/slide/infrastructure/module/independent/object-operation';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import ResizeMeasure from '@src/slide/domain/ui-model/element-modeles/property/text/editor-measure/resize-measure';
import { IOffset } from '@tencent/alloy-editor/lib/interfaces/render';
import {
    IElementCallback, IPresentationCallback,
    ISlideCallback, ISlideNode, ITableCellCallback,
    ITextBlockParentBoxCallback, ITextElementBoxCallback,
    ITextLineBoxCallback, ITextParagraphBoxCallback, SourcePageType,
} from './enumerator-types.interface';
import {
    IEnumerateCallback, IEnumerateOption,
    ISlideContentEnumeratorService,
} from '@src/slide/application/service/slide-content-enumerator/slide-content-enumerator-service.interface';

export class SlideContentEnumeratorService implements ISlideContentEnumeratorService {
    private uiDataService: IUiDataService;

    public constructor(uiDataService: IUiDataService) {
        this.uiDataService = uiDataService;
    }

    public async enumerateWithCallback(
        callback: IEnumerateCallback,
        options?: IEnumerateOption,
    ): Promise<void> {
        const { customUiDataService } = options || {};
        const uiDataService = customUiDataService || this.uiDataService;
        // 先转换出ui的数据
        await uiDataService.forceConvertSlides(uiDataService.presentation.children);
        callback.onStartEnumerate?.();
        await this.enumerateUiDataWithAllText(
            callback?.slideStartCallback,
            callback?.slideEndCallback,
            callback?.presentationEndCallback,
            callback?.elementCallback,
            callback?.tableCellCallback,
            callback?.textBlockParentBoxCallback,
            callback?.textParagraphParentBoxCallback,
            callback?.textLineParentBoxCallback,
            callback?.textElementBoxCallback,
            options,
        );
    }

    public enumerateWithCallbackSync(
        callback: IEnumerateCallback,
        options?: IEnumerateOption,
    ): void {
        callback.onStartEnumerate?.();
        this.enumerateUiDataWithAllText(
            callback?.slideStartCallback,
            callback?.slideEndCallback,
            callback?.presentationEndCallback,
            callback?.elementCallback,
            callback?.tableCellCallback,
            callback?.textBlockParentBoxCallback,
            callback?.textParagraphParentBoxCallback,
            callback?.textLineParentBoxCallback,
            callback?.textElementBoxCallback,
            options,
        );
    }

    private enumerateUiDataWithAllText(
        slideCallback?: ISlideCallback,
        slideEndCallback?: ISlideCallback,
        presentationEndCallback?: IPresentationCallback,
        elementCallback?: IElementCallback,
        tableCellCallback?: ITableCellCallback,
        textBlockParentBoxCallback?: ITextBlockParentBoxCallback,
        textParagraphParentBoxCallback?: ITextParagraphBoxCallback,
        textLineParentBoxCallback?: ITextLineBoxCallback,
        textElementBoxCallback?: ITextElementBoxCallback,
        options?: IEnumerateOption,
    ) {
        const { customUiDataService, slideModelList } = options || {};
        const uiDataService = customUiDataService || this.uiDataService;
        const slideList = slideModelList || uiDataService.slideModelList;
        const presentationModel = uiDataService.presentation;
        slideList?.forEach((slideModel, slideIndex) => {
            const slideNode = { slide: slideModel, slideIndex };
            slideCallback?.(slideNode);
            const masterElements = presentationModel.getMasterChildrenOmitPLByModel?.(slideModel);
            const layoutElements = presentationModel.getLayoutChildrenOmitPLByModel?.(slideModel);
            masterElements && this.enumerateUiDataElementsWithAllText(
                slideNode,
                UIModelType.MASTER,
                masterElements, elementCallback, tableCellCallback, textBlockParentBoxCallback,
                textParagraphParentBoxCallback, textLineParentBoxCallback, textElementBoxCallback,
                options,
            );
            layoutElements && this.enumerateUiDataElementsWithAllText(
                slideNode,
                UIModelType.LAYOUT,
                layoutElements, elementCallback, tableCellCallback, textBlockParentBoxCallback,
                textParagraphParentBoxCallback, textLineParentBoxCallback, textElementBoxCallback,
                options,
            );
            this.enumerateUiDataElementsWithAllText(
                slideNode,
                UIModelType.SLIDE,
                slideModel.children, elementCallback, tableCellCallback, textBlockParentBoxCallback,
                textParagraphParentBoxCallback, textLineParentBoxCallback, textElementBoxCallback,
                options,
            );
            slideEndCallback?.(slideNode);
        });
        presentationEndCallback?.(presentationModel);
    }

    private enumerateUiDataElementsWithAllText(
        slideNode: ISlideNode,
        sourcePageType: SourcePageType,
        elements: Array<IElementBaseModel>,
        elementCallback?: IElementCallback,
        tableCellCallback?: ITableCellCallback,
        textBlockParentBoxCallback?: ITextBlockParentBoxCallback,
        textParagraphParentBoxCallback?: ITextParagraphBoxCallback,
        textLineParentBoxCallback?: ITextLineBoxCallback,
        textElementBoxCallback?: ITextElementBoxCallback,
        options?: IEnumerateOption,
    ) {
        elements.forEach((element, elementIndex) => {
            if (element.hidden && options?.stopEnumerateHiddenElement) return;
            if (element.type === UIModelType.GROUP) {
                this.enumerateGroupWithAllText(
                    slideNode,
                    sourcePageType,
                    element as IGroupModel,
                    [elementIndex],
                    elementCallback,
                    textBlockParentBoxCallback,
                    textParagraphParentBoxCallback,
                    textLineParentBoxCallback,
                    textElementBoxCallback,
                    options,
                );
            } else if (element.type === UIModelType.TABLE) {
                elementCallback?.({ element, path: [elementIndex], slideNode, sourcePageType });
                const tableModel = element as ITableModel;
                for (let rowIndex = 0; rowIndex < tableModel.tableItems.length; rowIndex++) {
                    const cellArray = tableModel.tableItems[rowIndex];
                    cellArray?.forEach((cell, colIndex) => {
                        tableCellCallback?.({
                            path: [elementIndex, rowIndex, colIndex],
                            element,
                            cell,
                            slideNode,
                            col: colIndex,
                            row: rowIndex,
                        });
                    });
                }
            } else {
                this.enumerateLeafElementWithAllText(
                    slideNode,
                    sourcePageType,
                    element,
                    [elementIndex],
                    elementCallback,
                    textBlockParentBoxCallback,
                    textParagraphParentBoxCallback,
                    textLineParentBoxCallback,
                    textElementBoxCallback,
                    options,
                );
            }
        });
    }

    private enumerateGroupWithAllText(
        slideNode: ISlideNode,
        sourcePageType: SourcePageType,
        groupModel: IGroupModel,
        currentPath: Array<number>,
        elementCallback?: IElementCallback,
        textBlockParentBoxCallback?: ITextBlockParentBoxCallback,
        textParagraphParentBoxCallback?: ITextParagraphBoxCallback,
        textLineParentBoxCallback?: ITextLineBoxCallback,
        textElementBoxCallback?: ITextElementBoxCallback,
        options?: IEnumerateOption,
    ) {
        elementCallback?.({ element: groupModel, path: currentPath, slideNode, sourcePageType });
        groupModel.children.forEach((element, index) => {
            if (element.hidden && options?.stopEnumerateHiddenElement) return;
            const elementPath = currentPath.concat(index);
            if (element.type === UIModelType.GROUP) {
                this.enumerateGroupWithAllText(
                    slideNode,
                    sourcePageType,
                    element as IGroupModel,
                    elementPath,
                    elementCallback,
                    textBlockParentBoxCallback,
                    textParagraphParentBoxCallback,
                    textLineParentBoxCallback,
                    textElementBoxCallback,
                    options,
                );
            } else {
                this.enumerateLeafElementWithAllText(
                    slideNode,
                    sourcePageType,
                    element, elementPath,
                    elementCallback,
                    textBlockParentBoxCallback,
                    textParagraphParentBoxCallback,
                    textLineParentBoxCallback,
                    textElementBoxCallback,
                    options,
                );
            }
        });
    }

    private enumerateLeafElementWithAllText(
        slideNode: ISlideNode,
        sourcePageType: SourcePageType,
        elementModel: IElementBaseModel,
        currentPath: Array<number>,
        elementCallback?: IElementCallback,
        textBlockParentBoxCallback?: ITextBlockParentBoxCallback,
        textParagraphParentBoxCallback?: ITextParagraphBoxCallback,
        textLineParentBoxCallback?: ITextLineBoxCallback,
        textElementBoxCallback?: ITextElementBoxCallback,
        options?: IEnumerateOption,
    ) {
        elementCallback?.({ element: elementModel, path: currentPath, slideNode, sourcePageType });

        if (elementModel.type === UIModelType.SHAPE) {
            const shapeModel = elementModel as IShapeModel;
            if (isEditorEmpty(shapeModel.paragraphList)) return;
            const parser = shapeModel.pathParser;
            const { width, height } = shapeModel.props.style;
            const textRect: IRect = (parser as unknown as ShapeGraphic)
                .getTextRect(width, height)
                || { left: 0, top: 0, width, height };
            const editorDataModel = this.getTextEditorDataModel(shapeModel, textRect);
            const editorDataModelValue = editorDataModel.getEditorValue();

            // 构造出分析所需要的editor的排版信息
            const editorLayout: IDocumentBox = getTextLayout(
                editorDataModelValue,
                { width: textRect.width, height: textRect.height },
                { preciseMeasurement: true }, // 需要精确排版
            );
            const textBlockParentBoxPath: Array<number> = [];
            if (options?.stopTextBlockParentBoxCallback) return;
            textBlockParentBoxCallback?.({
                element: elementModel,
                textRect,
                textDocumentElement: editorDataModelValue,
                blockBox: editorLayout,
                path: currentPath,
                textBoxPath: [],
                slideNode,
            });
            traverseLayoutBoxWithHandlers(editorLayout, {
                [BoxKind.Paragraph]: (paragraphBox: IParagraphBox, offset: IOffset, path: number[]) => {
                    const textParagraphParentBoxPath: Array<number> = textBlockParentBoxPath.concat(path);
                    textParagraphParentBoxCallback?.({
                        element: elementModel,
                        textRect,
                        textDocumentElement: editorDataModelValue,
                        paraBox: paragraphBox,
                        path: currentPath,
                        textBoxPath: textParagraphParentBoxPath,
                        slideNode,
                    });
                },
                [BoxKind.TextLine]: (lineBox: ITextLineBox, offset: IOffset, path: number[]) => {
                    const textLineParentBoxPath: Array<number> = textBlockParentBoxPath.concat(path);
                    textLineParentBoxCallback?.({
                        element: elementModel,
                        textRect,
                        textDocumentElement: editorDataModelValue,
                        lineBox,
                        path: currentPath,
                        textBoxPath: textLineParentBoxPath,
                        slideNode,
                    });
                },
                [BoxKind.Text]: (textBox: ITextBox, offset: IOffset, path: number[]) => {
                    const textElementBoxPath: Array<number> = textBlockParentBoxPath.concat(path);
                    textElementBoxCallback?.({
                        element: elementModel,
                        textRect,
                        textDocumentElement: editorDataModelValue,
                        textBox,
                        path: currentPath,
                        textBoxPath: textElementBoxPath,
                        slideNode,
                    });
                },
            });
        }
    }

    private getTextEditorDataModel(shapeModel: IShapeModel, textRect: IRect): EditorDataModel {
        const props = {
            paragraphList: shapeModel.paragraphList,
            textBodyStyle: deepCopy(shapeModel.textBodyStyle),
            model: shapeModel,
            modelKey: shapeModel.key,
            isTitle: shapeModel.isTitleOrCtrTitlePl,
            textRect,
        };
        return new EditorDataModel({
            ...props,
            resizeLevel: ResizeMeasure.getResizeLevel(props),
            noSaveRawData: true, // 查看态不存rawData
        });
    }
}
